import { Link as RouterLink } from 'react-router-dom';

// material-ui
import { Box, Container, Divider, Link, Stack, Typography, alpha } from '@mui/material';

// project import
import { PageUrl } from 'const/page-url';

// ==============================|| COMPONENTS - FOOTER ||============================== //
//
// 直前に来るのがティールのCTA(ContactSection)なので、濃紺や黒にすると
// そこだけ急に暗く沈んで見える。サイト全体の「白＋ティール」に合わせた淡色にしている。

const primaryColor = '#2aa1af';

/** 外部リンクは href、サイト内は to を持たせる */
type FooterLink = { label: string; href?: string; to?: string; external?: boolean };

// ヘッダーのグローバルナビを置いていないため、サイト内の回遊とクローラーの巡回は
// ここが担っている。ページを増やしたらここにも必ず足すこと。
const links: FooterLink[] = [
  { label: 'ホーム', to: '/' },
  { label: '機能詳細', to: PageUrl.Features.urlWithSlash },
  { label: '資料請求', to: PageUrl.DocumentRequest.urlWithSlash },
  { label: 'お役立ち記事', to: PageUrl.Blog.urlWithSlash },
  { label: '会社概要', href: 'https://cocowa-tech.com', external: true },
  { label: 'プライバシーポリシー', to: PageUrl.PrivacyPolicy.urlWithSlash },
  { label: 'AIへの情報送信について', to: PageUrl.AiDataTransfer.urlWithSlash }
];

const Footer = () => {
  const linkSx = {
    color: '#4b5563',
    fontSize: '0.85rem',
    fontWeight: 600,
    '&:hover': { color: primaryColor }
  };

  const renderLink = (link: FooterLink) =>
    link.to ? (
      <Link key={link.label} component={RouterLink} to={link.to} underline="hover" sx={linkSx}>
        {link.label}
      </Link>
    ) : (
      <Link
        key={link.label}
        href={link.href}
        {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        underline="hover"
        sx={linkSx}
      >
        {link.label}
      </Link>
    );

  return (
    <Box
      component="footer"
      sx={{
        borderTop: `1px solid ${alpha(primaryColor, 0.18)}`,
        background: `linear-gradient(180deg, #f8fcfc 0%, ${alpha(primaryColor, 0.08)} 100%)`,
        color: '#4b5563',
        py: { xs: 4, md: 5 }
      }}
    >
      <Container>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2.5, md: 4 }}
            alignItems={{ xs: 'flex-start', md: 'flex-start' }}
            justifyContent="space-between"
          >
            {/* サービス名と運営会社。footer だけを見た人にも何のサイトか分かるようにしている */}
            <Stack spacing={0.75}>
              <Link
                component={RouterLink}
                to="/"
                underline="none"
                sx={{ color: primaryColor, fontWeight: 900, fontSize: '1.15rem', letterSpacing: '0.02em' }}
              >
                OHANA AI
              </Link>
              <Typography component="p" sx={{ color: '#6b7280', fontSize: '0.8rem', lineHeight: 1.6 }}>
                児童発達支援・放課後等デイサービス向け
                {/* スマホでは「〜向け」で改行して2行に収める */}
                <Box component="br" sx={{ display: { xs: 'inline', sm: 'none' } }} />
                書類作成AI
              </Typography>
              <Typography component="p" sx={{ color: '#374151', fontSize: '0.85rem', fontWeight: 700 }}>
                株式会社Cocowa
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.25, sm: 3 }}
              sx={{ pt: { md: 0.75 } }}
              flexWrap="wrap"
              useFlexGap
            >
              {links.map(renderLink)}
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: alpha(primaryColor, 0.18) }} />

          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
            &copy; {new Date().getFullYear()} Cocowa Inc. All rights reserved.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
